import { Add16 } from '@ingrid/iconography/react';
import { Button, ButtonLink, Card, ListLayout } from '@ingrid/luterkort-react';

import { ClearConfirmationModal } from '~src/components/ClearConfirmationModal/ClearConfirmationModal';
import { Weight } from '~src/components/Weight/Weight';
import { useTranslation } from '~src/i18n/use-translation';

import { useShipmentFormContext } from '../types';
import { CustomsDeclarationsAdd, Props as CustomsDeclarationsAddProps } from './customs-declarations-add';

export function CustomsDeclarations() {
  const { t } = useTranslation();
  const { watch, setValue } = useShipmentFormContext();
  const [customsDeclarations, parcels] = watch(['customs_declaration.general_customs_declaration', 'parcels']);

  const goods = parcels?.flatMap(p => p.contents?.goods).filter(l => !!l) ?? [];

  const customsDeclarationsAddProps: Omit<CustomsDeclarationsAddProps, 'trigger'> = {
    defaultValues: customsDeclarations,
    onSubmit: values => {
      setValue('customs_declaration.general_customs_declaration', values, { shouldDirty: true });
    },
    goods,
  };

  return (
    <Card.Wrapper as="section">
      <Card.Header>{t('common.customsDeclarations')}</Card.Header>
      <Card.Content>
        {customsDeclarations ? (
          <ListLayout.Stack>
            <ListLayout.Row>
              <ListLayout.PrimaryText>
                {customsDeclarations.incoterms
                  ? t(`shipments.customsDeclarations.incoterms.${customsDeclarations.incoterms}`)
                  : t('shipments.customsDeclarations.incoterms.INCOTERMS_UNKNOWN')}
              </ListLayout.PrimaryText>

              <ListLayout.Actions>
                <CustomsDeclarationsAdd
                  {...customsDeclarationsAddProps}
                  trigger={<ButtonLink>{t('common.edit')}</ButtonLink>}
                />
                <ClearConfirmationModal
                  clearedItemName="common.customsDeclarations"
                  onSubmit={() => setValue('customs_declaration.general_customs_declaration', undefined)}
                />
              </ListLayout.Actions>
            </ListLayout.Row>
            <ListLayout.SubRow gap="lg">
              <ListLayout.SecondaryText>
                {customsDeclarations.contents_type
                  ? t(`shipments.customsDeclarations.contentsType.${customsDeclarations.contents_type}`)
                  : t('shipments.customsDeclarations.contentsType.UNKNOWN')}
              </ListLayout.SecondaryText>

              {customsDeclarations.total_gross_weight && (
                <ListLayout.SecondaryText>
                  <Weight weight={customsDeclarations.total_gross_weight} />
                </ListLayout.SecondaryText>
              )}
            </ListLayout.SubRow>
          </ListLayout.Stack>
        ) : (
          <CustomsDeclarationsAdd
            {...customsDeclarationsAddProps}
            trigger={
              <Button variant="secondary">
                <Add16 /> {t('shipments.customsDeclarations.add')}
              </Button>
            }
          />
        )}
      </Card.Content>
    </Card.Wrapper>
  );
}
